function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import { ValueCache } from '@leverege/value-cache';
import { Pane, PropertyGrid, Content, Button } from '@leverege/ui-elements';
import { Config } from '@leverege/plugin';
import { TitleBar } from '@leverege/ui-plugin';
import { Attributes } from '@leverege/ui-attributes';
import FotaItemEditorUtil from "./FotaItemEditorUtil.js";
import S from './FotaItemEditor.css';
import { FotaAttributeEditorFactory } from "./attributeEditors/Factory.js";
export default class FotaItemEditor extends React.Component {
  static propTypes = {
    mode: PropTypes.string,
    attributeOptionKey: PropTypes.string,
    getAttributes: PropTypes.func,
    cancelText: PropTypes.string,
    submitText: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitFormat: PropTypes.string
  };
  static defaultProps = {
    mode: 'create',
    attributeOptionKey: 'createOptions',
    submitFormat: 'put',
    getAttributes: FotaItemEditorUtil.defaultGetAttributes,
    cancelText: 'Cancel',
    submitText: 'Submit'
  };
  constructor(props) {
    super(props);
    const {
      mode
    } = props;
    this.state = {
      breakpoint: this.getBreakPoint(window?.innerWidth)
    };
    this.cache = new ValueCache();
    if (mode === 'create') {
      this.state.changes = this.getDefaultValues();
    }
  }
  getBreakPoint(size) {
    return size > Config.get('ResponsiveUtility', 'smallBreakpoint', 1024) ? 'big' : 'small';
  }
  onResize(evt) {
    this.setState(state => {
      const newState = this.getBreakPoint(evt.target?.innerWidth);
      if (newState !== state.breakpoint) {
        return {
          breakpoint: newState
        };
      }
      return null;
    });
  }
  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }
  onSubmit = evt => {
    const {
      onSubmit,
      eventData,
      submitFormat
    } = this.props;
    let value;
    if (submitFormat === 'put') {
      value = this.getData();
    } else if (submitFormat === 'patch') {
      value = this.getPatch();
    }
    onSubmit?.({
      value,
      data: eventData,
      original: evt
    });
  };
  onCancel = evt => {
    const {
      onCancel,
      eventData
    } = this.props;
    onCancel?.({
      data: eventData,
      original: evt
    });
  };
  filterAttrs = attr => {
    const {
      mode
    } = this.props;
    let editable = false;
    if (mode === 'create') {
      editable = attr?.source?.creatable;
    }
    if (mode === 'edit') {
      editable = attr?.source?.editable;
    }
    return attr?.set && editable && !attr?.forwarded;
  };
  getAttributes() {
    const {
      getAttributes,
      target,
      attributeOptionKey
    } = this.props;
    return this.cache.calc((target, getAttributes) => {
      const attrs = getAttributes(target, this.filterAttrs) || [];
      // sort them
      attrs.sort(FotaItemEditorUtil.getSort(attributeOptionKey));
      return attrs;
    }, target, getAttributes);
  }

  // TODO: make compatible
  // buildValidator() {
  //   const attributes = this.getAttributes( )    
  //   const validators = { fields : {} }

  //   attributes.forEach( ( attr ) => {
  //     const validatorType = attr?.source?.attributeOptionKey?.validator || attr.name
  //     const field = attr?.blueprint?.updatePath
  //     const validator = null // BlueprintAttributeFormValidatorFactory.create( validatorType, attr )
  //     if ( validator ) {
  //       validators.fields[field] = validator
  //     }
  //   } )
  //   return validators
  // }

  onFieldChange = ({
    value,
    data
  }) => {
    const {
      attribute
    } = data;
    this.setState(state => ({
      changes: {
        ...state.changes,
        [attribute.name]: value
      }
    }));
  };
  getContext() {
    return this.props;
  }
  getDefaultValues() {
    const {
      target
    } = this.props;
    const attrs = this.getAttributes();
    const changes = {};
    attrs.forEach(attr => {
      if (attr.get(target?.data) === undefined) {
        const defaultValue = attr?.getDefaultValue?.(target);
        if (defaultValue !== undefined) {
          changes[attr.name] = defaultValue;
        }
      }
    });
    return changes;
  }
  getData() {
    const {
      target
    } = this.props;
    const {
      changes
    } = this.state;
    let toReturn = target?.data;
    if (changes) {
      Object.keys(changes).forEach(attrName => {
        const attr = Attributes.getAttribute(attrName, target?.type);
        toReturn = attr.set(toReturn, changes[attrName], this.getContext());
      });
    }
    return toReturn;
  }
  getPatch() {
    const {
      target,
      data
    } = this.props;
    const {
      changes
    } = this.state;
    const toReturn = [];
    if (changes) {
      Object.keys(changes).forEach(attrName => {
        const attr = Attributes.getAttribute(attrName, target?.type);
        const change = attr.getPatch?.(data, changes[attrName], this.getContext());
        if (Array.isArray(change)) {
          toReturn.push(...change);
        } else if (change != null) {
          toReturn.push(change);
        }
      });
    }
    return toReturn;
  }
  getAttributeEditorLabel(attr) {
    const {
      attributeOptionKey
    } = this.props;
    return attr?.source?.[attributeOptionKey]?.label || attr?.displayName || attr?.name;
  }
  getEditor = attr => {
    const {
      attributeOptionKey,
      model
    } = this.props;
    const editorOptions = model?.editors?.[attr.name];
    const editorType = editorOptions?.editor ?? attr?.source?.[attributeOptionKey]?.editor ?? attr?.valueType;
    const editorProps = attr?.source?.[attributeOptionKey]?.editorProps;
    const containerProps = attr?.source?.[attributeOptionKey]?.containerProps;
    const innerEditor = FotaAttributeEditorFactory.create(editorType, {
      attr,
      onChange: this.onFieldChange,
      data: this.getData(),
      context: this.getContext(),
      ...editorProps,
      ...model?.editors?.[attr.name]?.props
    });
    if (!innerEditor || editorOptions?.enabled === false) {
      return null;
    }
    return /*#__PURE__*/React.createElement(PropertyGrid.Item, _extends({
      key: attr.name,
      label: this.getAttributeEditorLabel(attr),
      fill: true
    }, containerProps), innerEditor);
  };
  render() {
    const {
      variant,
      titleVariant = 'dialogTitle',
      title,
      titleIcon,
      submitText,
      cancelText,
      headerVariant,
      bodyVariant,
      footerVariant
    } = this.props;
    const {
      breakpoint
    } = this.state;
    const vnt = variant || (breakpoint === 'small' ? 'mobileItemForm' : 'itemForm');
    const attributes = this.getAttributes();
    const editors = attributes.map(this.getEditor).filter(e => e);
    const headerVnt = headerVariant || (breakpoint === 'small' ? 'mobileItemFormHeader' : 'itemFormHeader');
    const bodyVnt = bodyVariant || (breakpoint === 'small' ? 'mobileItemFormBody' : 'itemFormBody');
    const footerVnt = footerVariant || (breakpoint === 'small' ? 'mobileItemFormFooter' : 'itemFormFooter');
    return /*#__PURE__*/React.createElement(Pane, {
      variant: vnt
    }, /*#__PURE__*/React.createElement(Content, {
      relative: breakpoint === 'big'
    }, /*#__PURE__*/React.createElement(Content.Header, {
      variant: `${headerVnt}|formHeader`
    }, /*#__PURE__*/React.createElement(TitleBar, {
      variant: titleVariant,
      title: title,
      icon: titleIcon
    })), /*#__PURE__*/React.createElement(Content.Area, {
      variant: `${bodyVnt}|formBody`,
      className: S.form
    }, /*#__PURE__*/React.createElement(PropertyGrid, {
      inline: false,
      vertical: true,
      className: S.fullWidth
    }, editors)), /*#__PURE__*/React.createElement(Content.Footer, {
      variant: `${footerVnt}|formButtons`,
      layout: "flex:rowMEnd"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "secondary",
      type: "button",
      onClick: this.onCancel
    }, cancelText), /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      onClick: this.onSubmit
    }, submitText))));
  }
}